import React, {useCallback, useEffect, useLayoutEffect, useState} from "react";
import {Row} from "reactstrap";
import AddPartSupportRequestDefineSection from "./sections/define";
import Dialog from "../../../base/dialog";
import AddPartSupportRequestLookupSection from "./sections/lookup";
import AddPartSupportRequestPartInfoSection from "./sections/part-info";
import UpsertPartSupportRequestDocumentation from "../_common/documentation";
import TopBarProgressIndicator from "../../../app-specific/topbar-progress-indicator";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";


const initDefineValues = {
    manualRga: false,
    rga: undefined,
    inventoryBased: false,
    customer: undefined,
    type: undefined,
    warehouse: undefined,
}

const initLookupValues = {
    showProductOrSerialNoSelection: false,
    invoiceNo: null,
    warehouseCode: null,
    part: undefined,
    isSerialized: false,
    serialNumber: undefined,
    selectedQuantity: null,
}

const initPartInfoValues = {}


export const AddPartSupportRequestProcessSteps = {
    define: 'Define',
    lookup: 'Lookup',
    partInfo: 'Part Information',
    documentation: 'Documentation',
}

const AddPartSupportRequestDialog = ({
                                         open,
                                         setOpen,
                                         types,
                                         loadingTypes,
                                         onSuccess,
                                         request,
                                         currentProcess: currentProcessProps = AddPartSupportRequestProcessSteps.define,
                                     }) => {
    const [currentProcess, setCurrentProcess] = useState(currentProcessProps ?? AddPartSupportRequestProcessSteps.define);
    const [defineValues, setDefineValues] = useState(initDefineValues);
    const [lookupValues, setLookupValues] = useState(initLookupValues);
    const [partInfoValues, setPartInfoValues] = useState(initPartInfoValues);
    const [newRequest, setNewRequest] = useState(request ?? {});
    const [warehouses, setWarehouses] = useState([]);
    const isMounted = useIsMounted();

    /**
     * With each change in the open state of the dialog and currentProcess props:
     * - if dialog is open, sync the current process's state value with the given currentProcessProps;
     */
    useLayoutEffect(() => {
        if (!open)
            return;
        setCurrentProcess(currentProcessProps ?? AddPartSupportRequestProcessSteps.define);
    }, [currentProcessProps, open])

    /**
     * With each change in the open state of the dialog and request props:
     * - if dialog is open, sync the new request's state value with the given request props;
     */
    useLayoutEffect(() => {
        if (!open)
            return;
        setNewRequest(request ?? {});
    }, [request, open])

    /**
     * Fetches all the warehouses for the current partner.
     * @type {function(): void}
     */
    const getAllWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * Resets the state of the modal as soon as the process successful finishes and calls onSuccess with the
     * returned request.
     * @type {function(*=): void}
     */
    const terminateProcess = useCallback((createdRequest = null) => {
        setCurrentProcess(AddPartSupportRequestProcessSteps.define);
        setDefineValues(initDefineValues);
        setLookupValues(initLookupValues);
        setPartInfoValues(initPartInfoValues);
        setNewRequest({});
        if (createdRequest) {
            onSuccess(createdRequest)
        }
        setOpen(false);
    }, [onSuccess, setOpen])


    /**
     * Listens for the changes in open and with each change:
     * if not open then empties the list of duplicated RGAs.
     */
    useEffect(() => {
        if (!open) {
            return terminateProcess(null)
        }
        void getAllWarehouses();
    }, [getAllWarehouses, open, terminateProcess])

    /**
     * Determines if a ball has been passed over relative to their order in ordering process.
     * @type {function(*, *): boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case AddPartSupportRequestProcessSteps.define:
            default:
                return false
            case AddPartSupportRequestProcessSteps.lookup:
                return (type === AddPartSupportRequestProcessSteps.define);
            case AddPartSupportRequestProcessSteps.partInfo:
                return (type === AddPartSupportRequestProcessSteps.define || type === AddPartSupportRequestProcessSteps.lookup);
            case AddPartSupportRequestProcessSteps.documentation:
                return (type === AddPartSupportRequestProcessSteps.define || type === AddPartSupportRequestProcessSteps.lookup || type === AddPartSupportRequestProcessSteps.partInfo);
        }
    }, [])


    return (
        <Dialog
            disableBackdropClick={currentProcess !== AddPartSupportRequestProcessSteps.define}
            open={open}
            setOpen={setOpen}
            className={'add-warranty-and-return-dialog'}
            maxWidth={'md'}
        >
            <TopBarProgressIndicator
                types={AddPartSupportRequestProcessSteps}
                currentType={currentProcess}
                passed={passed}
                className={'dialog-top-section'}
            />
            <Row className='no-gutters justify-content-center align-items-start w-100 mt--4'>
                {
                    currentProcess === AddPartSupportRequestProcessSteps.define &&
                    <AddPartSupportRequestDefineSection
                        setProcess={setCurrentProcess}
                        defineValues={defineValues}
                        setDefineValues={setDefineValues}
                        loadingTypes={loadingTypes}
                        types={types}
                        warehouses={warehouses}
                        setOpen={setOpen}
                    />
                }
                {
                    currentProcess === AddPartSupportRequestProcessSteps.lookup &&
                    <AddPartSupportRequestLookupSection
                        defineValues={defineValues}
                        lookupValues={lookupValues}
                        setLookupValues={setLookupValues}
                        setPartInfoValues={setPartInfoValues}
                        setProcess={setCurrentProcess}
                        terminateProcess={terminateProcess}
                    />
                }
                {
                    currentProcess === AddPartSupportRequestProcessSteps.partInfo &&
                    <AddPartSupportRequestPartInfoSection
                        defineValues={defineValues}
                        partInfoValues={partInfoValues}
                        setLookupValues={setLookupValues}
                        setNewRequest={setNewRequest}
                        setProcess={setCurrentProcess}
                    />
                }
                {
                    currentProcess === AddPartSupportRequestProcessSteps.documentation &&
                    <UpsertPartSupportRequestDocumentation
                        warehouseCode={defineValues.warehouse?.code}
                        terminateProcess={terminateProcess}
                        request={newRequest}
                    />
                }
            </Row>
        </Dialog>
    );
}


export default AddPartSupportRequestDialog;
